import React from "react";
import { Filter, Search, SlidersHorizontal, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const ConferenceAttendeeFiltersDrawer = ({
  isOpen,
  onClose,
  searchTerm,
  paymentStatusFilter,
  verificationFilter,
  registrationTypeFilter,
  ticketTypeFilter,
  countryFilter,
  locationFilter,
  sortBy,
  sortOrder,
  registrationTypeOptions,
  ticketTypeOptions,
  countryOptions,
  locationOptions,
  onSearchChange,
  onPaymentStatusChange,
  onVerificationChange,
  onRegistrationTypeChange,
  onTicketTypeChange,
  onCountryChange,
  onLocationChange,
  onSortByChange,
  onSortOrderToggle,
  onClearFilters,
  activeFilterCount = 0,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50">
          <motion.div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          <motion.aside
            className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl border-l border-slate-200 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", ease: "easeOut", duration: 0.28 }}
          >
        <div className="px-6 py-5 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filter Attendees
              </h3>
              <p className="mt-1 text-blue-100 text-sm">
                Refine by payment, registration type, ticket, location, and sort options.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/15 transition-colors"
              aria-label="Close filters drawer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-700">
              Active filters: <span className="font-semibold">{activeFilterCount}</span>
            </div>
            <button
              type="button"
              onClick={onClearFilters}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
            >
              Clear All
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <section className="rounded-xl border border-slate-200 p-4 bg-white shadow-sm">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Name, email, phone, location"
              />
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 p-4 bg-white shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
              Core Filters
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <select
                value={paymentStatusFilter}
                onChange={(e) => onPaymentStatusChange(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Payment Status</option>
                <option value="pending">Pending Only</option>
              </select>

              <select
                value={verificationFilter}
                onChange={(e) => onVerificationChange(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Verification</option>
                <option value="verified">Verified Only</option>
                <option value="unverified">Unverified Only</option>
              </select>

              <select
                value={registrationTypeFilter}
                onChange={(e) => onRegistrationTypeChange(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Registration Types</option>
                {registrationTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={ticketTypeFilter}
                onChange={(e) => onTicketTypeChange(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Ticket Types</option>
                {ticketTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={countryFilter}
                onChange={(e) => onCountryChange(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Countries</option>
                {countryOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              <select
                value={locationFilter}
                onChange={(e) => onLocationChange(e.target.value)}
                className="px-3 py-2.5 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Locations</option>
                {locationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 p-4 bg-white shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Sort Options
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { value: "name", label: "Name" },
                { value: "email", label: "Email" },
                { value: "location", label: "Location" },
                { value: "verification", label: "Verification" },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onSortByChange(option.value)}
                  className={`px-3 py-2 text-xs font-semibold rounded-lg border transition-colors ${
                    sortBy === option.value
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={onSortOrderToggle}
              className="w-full px-3 py-2.5 rounded-lg border border-slate-300 bg-slate-50 hover:bg-slate-100 text-sm font-medium text-slate-700 transition-colors"
            >
              {sortOrder === "asc" ? "Ascending (A-Z)" : "Descending (Z-A)"}
            </button>
          </section>
        </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ConferenceAttendeeFiltersDrawer;

